import { Box, Card, Divider, Switch, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetUserCustomersForNotification } from 'src/api/customer'
import { useGetMyNotificationPreferences, useUpdateMyNotificationPreferences } from 'src/api/notification-preference'
import { INotificationPreference, NotificationCategory } from 'src/generated-types/notification'
import { CustomerScopeSection } from './CustomerScopeSection'

interface NotificationPreferencesProps {
  customerId: EntityId
}

const CATEGORY_LABEL: Record<NotificationCategory, string> = {
  [NotificationCategory.Deviations]: 'notify-deviations',
  [NotificationCategory.WorkOrders]: 'notify-work-orders',
  [NotificationCategory.Service]: 'notify-service',
  [NotificationCategory.Certificates]: 'notify-certificates',
  [NotificationCategory.Reports]: 'notify-reports',
  [NotificationCategory.WorkAssignments]: 'notify-work-assignments',
}

// Categories that distinguish "new" from "updated".
const CATEGORIES_WITH_UPDATES = [
  NotificationCategory.Deviations,
  NotificationCategory.WorkOrders,
  NotificationCategory.WorkAssignments,
]

/**
 * Self-service notification settings for the user's profile: in-app / email / updates
 * toggles per category (user-global), plus a per-customer locality & group scope.
 */
export const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({ customerId }) => {
  const { t } = useTranslation('common')
  const { data } = useGetMyNotificationPreferences(customerId)
  const { data: userCustomers } = useGetUserCustomersForNotification()
  const { mutateAsync: savePreferences, isLoading: saving } = useUpdateMyNotificationPreferences(customerId)
  const [prefs, setPrefs] = useState<INotificationPreference[]>([])

  useEffect(() => {
    if (data) setPrefs(data)
  }, [data])

  const customers = useMemo(() => {
    const items = userCustomers ?? []
    if (items.length > 0) return items.map(c => ({ id: c.id as EntityId, name: c.name }))
    return [{ id: customerId, name: undefined as string | undefined }]
  }, [userCustomers, customerId])

  const toggle = async (
    category: NotificationCategory,
    channel: 'inApp' | 'email' | 'includeUpdates',
    value: boolean,
  ) => {
    const includeUpdates = CATEGORIES_WITH_UPDATES.includes(category)
    let updates = prefs.find(p => p.category === category)?.includeUpdates ?? false
    if (
      includeUpdates &&
      ((channel === 'inApp' && !value && !prefs.find(p => p.category === category)?.email) ||
        (channel === 'email' && !value && !prefs.find(p => p.category === category)?.inApp))
    ) {
      updates = false
    }
    const next = prefs.map(p => (p.category === category ? { ...p, [channel]: value, includeUpdates: updates } : p))
    setPrefs(next)
    await savePreferences({ preferences: next })
  }

  const handleToggleForUpdates = async (category: NotificationCategory, value: boolean) => {
    const email = prefs.find(p => p.category === category)?.email
    const next = prefs.map(p =>
      p.category === category ? { ...p, includeUpdates: value, inApp: value && !email ? true : p.inApp } : p,
    )
    setPrefs(next)
    await savePreferences({ preferences: next })
  }

  const columnSx = { width: 64, textAlign: 'center' as const, flexShrink: 0 }
  // The Updates toggle is a different kind of control (scope, not channel), so set it apart.
  const updatesColSx = { ...columnSx, borderLeft: '1px solid', borderColor: 'divider' }

  return (
    <Card sx={{ p: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="h6" fontWeight={600} mb={1}>
        {t('notification-preferences')}
      </Typography>
      <Divider sx={{ mb: 1 }} />
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('notify-channels-hint')}
      </Typography>
      <Box display="flex" alignItems="center" pb={0.5}>
        <Box flex={1} />
        <Typography variant="caption" color="text.secondary" sx={columnSx}>
          {t('in-app')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={columnSx}>
          {t('email')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={updatesColSx}>
          {t('include-updates')}
        </Typography>
      </Box>
      {prefs.map(pref => (
        <Box key={pref.category} display="flex" alignItems="center" py={0.25}>
          <Typography variant="body2" flex={1}>
            {t(CATEGORY_LABEL[pref.category])}
          </Typography>
          <Box sx={columnSx}>
            <Switch
              size="small"
              checked={pref.inApp}
              disabled={saving}
              onChange={e => toggle(pref.category, 'inApp', e.target.checked)}
            />
          </Box>
          <Box sx={columnSx}>
            <Switch
              size="small"
              checked={pref.email}
              disabled={saving}
              onChange={e => toggle(pref.category, 'email', e.target.checked)}
            />
          </Box>
          <Box sx={updatesColSx}>
            {CATEGORIES_WITH_UPDATES.includes(pref.category) && (
              <Switch
                size="small"
                checked={pref.includeUpdates}
                disabled={saving}
                onChange={e => {
                  handleToggleForUpdates(pref.category, e.target.checked)
                }}
              />
            )}
          </Box>
        </Box>
      ))}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="subtitle2" fontWeight={600}>
        {t('notify-localities')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('notify-localities-hint')}
      </Typography>
      {customers.map(c => (
        <CustomerScopeSection key={c.id} customerId={c.id} customerName={c.name} showName={customers.length > 1} />
      ))}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {t('notifications-follow-tip')}
      </Typography>
    </Card>
  )
}

export default NotificationPreferences
